import "./modules/cartLink.js"
import "./modules/cartAccordion.js"
import { FetchError, SHOPIFY_GENERIC_ERROR } from "./lib/fetchJson.js";
import {
	fetchCart,
	postCartChange,
	postCartClear,
	postCartUpdate,
} from "./lib/cartApi.js";
import { debounce, sleep } from "./lib/utils.js";
import { Attributes, Cart, CartUpdateInput } from "./types/Shopify.js";
import { clampOrderQuantity, readQuantityBound } from "./lib/clampOrderQuantity.js";
import { formatMoney } from "./lib/formatMoney.js";
import { trackCartLegacyEvent } from "./lib/analytics.js";

declare global {
  interface Window {
		ffLineItems: {
			key?: string
			lead?: string
			properties: [string,string][]
			quantity?: number
		}[]
		ffCart: FFCart
  }
}

//TODO
//add loading gif when prepopulating cart
class FFCart {
	cart?: Cart

  DOM: {
    poInput: HTMLInputElement | null
    cartList: HTMLElement | null
    qts: NodeListOf<HTMLElement>
    noteInput: HTMLInputElement | null
    form: HTMLElement | null
    submit: HTMLElement | null
    clearCart: HTMLElement | null
    removeButtons: NodeListOf<HTMLElement>
    states: HTMLElement | null
    state_empty: HTMLElement | null
    state_full: HTMLElement | null
    header_cart: HTMLElement | null
    checkout: HTMLElement | null
		totalPrice: HTMLElement | null
		messageWrapper: HTMLElement | null
  }

	constructor(){
		this.DOM = {
			poInput: document.querySelector(".ffjs-poNumber"),
      cartList: document.querySelector('.cart-list'),
      qts: document.querySelectorAll('.cart-list .qty'),
      noteInput: document.querySelector('#cartSpecialInstructions'),
      form: document.querySelector('#ff-form-cart'),
      submit: document.querySelector("#ff-form-cart input[type=submit]"),
      clearCart: document.querySelector('.js-clearCart'),
      removeButtons: document.querySelectorAll('.ff-js-cart-remove'),
      states: document.querySelector('#cart-states'),
      state_empty: document.querySelector('#ff-cart-empty'),
      state_full: document.querySelector('#ff-form-cart'),
      header_cart: document.querySelector('.js-cartHeader'),
      checkout: document.querySelector('[name="checkout"]'),
			totalPrice: document.querySelector('.total-price .price'),
			messageWrapper: document.querySelector('.js-cartMessages'),
		};

		this.events();
	}

	async init(){
		await this.setCart()
		await this.checkQuery()
		await this.enforceQuantityFloors()
		await this.updateCartState()
		this.trackLegacySnapshot()
		void this.setLeadTimeProperties()
	}

	events(){
		if( this.DOM.poInput ){
			const debouncedPOChange = debounce(this.poChange.bind(this), 250);
			this.DOM.poInput.addEventListener('input', debouncedPOChange);
		}
		//Line item events
		this.DOM.qts.forEach(el => {
			el.addEventListener('change', this.updateQuantity.bind(this) );
		})
		this.DOM.removeButtons.forEach(el => {
			el.addEventListener('click', this.removeClick.bind(this) );
		})
		this.DOM.clearCart?.addEventListener('click', this.clearCart.bind(this) );
	}

	async updateCart(data: CartUpdateInput): Promise<Cart | null>{
		try {
			return await postCartUpdate(data)
		} catch (error) {
			if (error instanceof FetchError) {
				if (error.status === 404) {
					await this.handle404(data);
				} else {
					this.showCartError(SHOPIFY_GENERIC_ERROR);
				}
			}
		}
		return null
	}

	async updateQuantity(e: Event){
		const target = e.target;
		if( !(target instanceof HTMLInputElement) ) return
		const quantityEl = target.closest("ff-quantity");
		const min = readQuantityBound(quantityEl?.getAttribute("min"));
		const max = readQuantityBound(quantityEl?.getAttribute("max"));
		const qty = clampOrderQuantity(Number(target.value), min, max);
		if (Number.isFinite(qty) && target.value !== String(qty)) {
			target.value = String(qty);
		}
		const entry = target.closest('li');
		const price = entry?.querySelector('.price');
		const variantId = entry?.getAttribute('data-var-id') || ""; 

		const updatedCartData = await this.updateCart({ 
			updates: { 
				[variantId]: qty
			} 
		});
		if( !updatedCartData) return

		this.updateTotalPrice( updatedCartData.total_price )
		this.DOM.checkout?.removeAttribute("disabled");
		const event = new CustomEvent('ffCartChange', { detail: updatedCartData } );
		document.dispatchEvent(event);

		const cartItem = updatedCartData.items.find(item => String(item.variant_id) === variantId);

		//update line total
		if( price instanceof HTMLElement ){
			const linePriceCents = cartItem?.line_price ?? Number(price.getAttribute('data-price')) * qty;
			price.innerHTML = formatMoney(linePriceCents, "${{amount}}");
		}

		this.updateBundleComponentQuantities(entry, cartItem?.quantity ?? qty)
	}

	async enforceQuantityFloors(){
		const updates: { [variantId: string]: number } = {}
		this.DOM.qts.forEach(el => {
			if (!(el instanceof HTMLInputElement)) return
			const quantityEl = el.closest("ff-quantity")
			const min = readQuantityBound(quantityEl?.getAttribute("min"))
			const max = readQuantityBound(quantityEl?.getAttribute("max"))
			const current = Number(el.value)
			const qty = clampOrderQuantity(current, min, max)
			if (!Number.isFinite(qty) || qty === current) return
			el.value = String(qty)
			const variantId = el.closest("li")?.getAttribute("data-var-id")
			if (!variantId) return
			updates[variantId] = qty
		})
		if (Object.keys(updates).length === 0) return

		const updatedCartData = await this.updateCart({ updates })
		if (!updatedCartData) return
		this.cart = updatedCartData
		this.updateTotalPrice(updatedCartData.total_price)

		for (const [variantId, qty] of Object.entries(updates)) {
			const entry = this.DOM.cartList?.querySelector(`[data-var-id="${variantId}"]`)
			const price = entry?.querySelector(".price")
			const cartItem = updatedCartData.items.find(item => String(item.variant_id) === variantId)
			if (price instanceof HTMLElement) {
				const linePriceCents = cartItem?.line_price ?? Number(price.getAttribute("data-price")) * qty
				price.innerHTML = formatMoney(linePriceCents, "${{amount}}")
			}
			this.updateBundleComponentQuantities(entry, cartItem?.quantity ?? qty)
		}
	}

	updateBundleComponentQuantities( entry: Element | null | undefined, qty: number ){
		if( !(entry instanceof HTMLElement) || !Number.isFinite(qty) ) return

		entry.querySelectorAll<HTMLElement>('[data-bundle-unit-qty]').forEach(el => {
			const unit = Number(el.dataset.bundleUnitQty)
			const qtyEl = el.querySelector('.js-bundleComponentQty')
			if( !Number.isFinite(unit) || !(qtyEl instanceof HTMLElement) ) return
			qtyEl.textContent = String(unit * qty)
		})
	}

	//https://store.freeflysystems.com/cart?ff_add=1&id[]=42072815751&id[]=42072815751&id[]=42072815751&id[]=42072815751&id[]=42072815815&id[]=42072815815&id[]=42072815815&id[]=42072815815&id[]=42072816903&id[]=701957937
	async setCart(){
		try {
			this.cart = await fetchCart()
		} catch (_error) {
			// fetchJson reports errors to Sentry
		}
	}

	async poChange(e: Event){
		if( !(e.target instanceof HTMLInputElement )) return
		const value = e.target.value
		await this.updateAttribute( 'PO-Number', value)
	}

	async updateAttribute(name: string, value: string){
		return this.updateCart({
			attributes: {
				[name]: value
			}
		})
	}

	async checkQuery(){
		const path = window.location.href;
		let queryString = ( path.split('?').length > 1 )? path.split('?')[1]:'';

		if( queryString.length === 0 ) return;
		queryString = decodeURIComponent(queryString)

		if( queryString.indexOf('expired=1') > -1 ){
			trackCartLegacyEvent('cart_custom_order_expired')
			this.showCartError('Your custom cart link is expired. Please contact <a href="mailto:support@freeflysystems.com">support@freeflysystems.com</a> <span class="no-wrap">for assistance.</span>');
		}else{
			await this.updateCartIfNeeded( queryString );
		}
	}

	getUpdatesObject( query: string ){
		return query.split(',').reduce((acc, cur) => {
			if( cur.indexOf(':') === -1 ) return acc
			const parts = cur.split(":")
			const variantId = parts[0];
			const quantity = parts[1];
			const itemAlreadyInCart = !!this.cart?.items.find( el => el.id === parseInt(variantId))
			if( itemAlreadyInCart ) return acc
			return {
				...acc,
				[variantId]: parseInt(quantity),
			}
		}, {} as {[variantId: string]: number})
	}

	async updateCartIfNeeded( queryString: string ){
		const postData: {
			note?: string
			attributes?: Attributes
			updates?: {[variantId: string]: number}
		} = {};
		const shouldAddItems = ( queryString.indexOf('items=') !== -1 );
		const shouldAddAttributes = ( queryString.indexOf('attributes') !== -1 );

		const parameters: Attributes = {};
		const queryParts = queryString.split('&');
		for (let i = queryParts.length - 1; i >= 0; i--) {
			const parameterName = queryParts[i].split('=')[0];
			const parameterValue = queryParts[i].split('=')[1];
			parameters[parameterName] = parameterValue;
		}

		//Add Note
		if( Object.hasOwn(parameters, 'note') ){
			postData['note'] = parameters.note;
		}

		//Add Attributes
		if( shouldAddAttributes ){
			const attributes: Attributes = {};
			for( let parameterName in parameters ){
				if( parameterName.indexOf('attributes') === -1 ) continue
				const input = document.querySelector('input[name="' + parameterName + '"]');
				if( !input ) continue
				const parameterValue = parameters[parameterName]
				parameterName = parameterName.replace('attributes[', '');
				parameterName = parameterName.replace(']', '');
				attributes[parameterName] = parameterValue;
			}
			if( Object.keys(attributes).length > 0 ){
				postData['attributes'] = attributes;
			}
		}

		//Prepare to add items to cart
		let updates = {}
		if( shouldAddItems ){
			updates = this.getUpdatesObject( parameters.items );
			if( Object.keys(updates).length > 0 ){
				postData['updates'] = updates;
			}
		}

		let redirectLocation = '/cart'
		if( Object.hasOwn(parameters, 'discount') ){
			redirectLocation = `/discount/$parameters.discount}?redirect=/cart`
		}

		if( Object.keys(postData).length > 0 ){
			trackCartLegacyEvent('cart_query_hydrate', {
				has_items: !!postData.updates,
				has_attributes: !!postData.attributes,
				has_note: !!postData.note,
				has_discount: Object.hasOwn(parameters, 'discount'),
				item_update_count: Object.keys(updates).length,
			})
			const data = await this.updateCart( postData )
			if( !data ) return
			this.cart = data
			document.location = redirectLocation;
		}
	}

	async handle404(data: CartUpdateInput){
		const notFound: string[] = []
		for (const id in data.updates) {
			if (Object.hasOwn(data.updates, id)) {
				try {
					await postCartUpdate({ updates: { [id]: data.updates[id] } })
			  } catch(_err) {
			    notFound.push(id);
			  }
			}
		}

		this.showCartError( 'Variant id(s) <em>' + notFound.join(', ') + '</em> could not be added. Please contact <a href="mailto:support@freeflysystems.com">support@freeflysystems.com</a> <span class="no-wrap">for assistance.</span>');
	}

	clearAttributes(){
		const data = {
			attributes: {
				'ra-number': '',
				'PO-Number': '',
				'Shipping-Method': '',
				Account: '',
				Carrier: '',
			},
			note: ''
		};
		return this.updateCart(data)
	}

	async clearItems(): Promise<Cart | null>{
		try {
			return await postCartClear()
		} catch (_error) {
			// fetchJson reports errors to Sentry
		}
		return null
	}

	updateTotalPrice( val: number ){
		if( !(this.DOM.totalPrice instanceof HTMLElement) ) return
		this.DOM.totalPrice.innerHTML = formatMoney( val, "${{amount}}" );
	}

	async removeClick(e: MouseEvent){
		const target = e.currentTarget;
		if( !(target instanceof HTMLElement) ) return
		const li = target.parentNode?.parentNode?.parentNode;
		const variantId = target.getAttribute('data-var-id');
		if( !(li instanceof HTMLElement) || !variantId ) return

		const updatedCartData = await this.updateCart({ updates: { [variantId]: 0 } });
		if( !updatedCartData ) return
		this.updateTotalPrice( updatedCartData.total_price )
		this.cart = updatedCartData
		const event = new CustomEvent('ffCartChange', { detail: updatedCartData } );
		document.dispatchEvent(event);

		li.style.height = li.offsetHeight + 'px';
		await sleep(10)
		li.classList.add('ff-js-condemn');
		await sleep(500)
		li.parentNode?.removeChild(li);
		await this.updateCartState()
	}

	async updateCartState(){
		const hasItems = this.cart
			? this.cart.item_count > 0
			: document.querySelectorAll('.cart-list .item-cart').length > 0
		if( hasItems ){
			await this.showFullCart();
		}else{
			await this.showEmptyCart();
		}
	}

	async showEmptyCart(){
		if( !(this.DOM.states instanceof HTMLElement) || !(this.DOM.state_empty instanceof HTMLElement)) return
		this.DOM.states.style.height = this.DOM.states.clientHeight + 'px';
		this.DOM.state_full?.classList.remove('js-active');
		this.DOM.state_empty?.classList.add('js-active');
		this.DOM.header_cart?.classList.remove('cart-full');
		this.DOM.states.style.height = this.DOM.state_empty.clientHeight + 'px';
		await sleep(500)
		this.DOM.states?.removeAttribute('style');
	}

	async showFullCart(){
		if( !(this.DOM.states instanceof HTMLElement) || !(this.DOM.state_full instanceof HTMLElement)) return
		this.DOM.states.style.height = this.DOM.states.clientHeight + 'px';
		this.DOM.state_empty?.classList.remove('js-active');
		this.DOM.state_full?.classList.add('js-active');
		this.DOM.header_cart?.classList.add('cart-full');
		this.DOM.states.style.height = this.DOM.state_full.clientHeight + 'px';
		await sleep(500)
		this.DOM.states?.removeAttribute('style');
	}


	async clearCart(e: MouseEvent){
		e.preventDefault();
		
		//hide/remove elements
		this.showEmptyCart();
		if( this.DOM.cartList ){
			this.DOM.cartList.innerHTML = '';
		}

		await this.clearAttributes()
		const data = await this.clearItems()
		const event = new CustomEvent('ffCartChange', { detail: data } );
		document.dispatchEvent(event);
	}

	trackLegacySnapshot(){
		const query = window.location.search

		trackCartLegacyEvent('cart_legacy_snapshot', {
			has_query: query.length > 1,
			has_items_param: query.includes('items='),
			is_expired_link: query.includes('expired=1'),
			item_count: this.cart?.item_count ?? 0,
		})
	}

	showCartError( message: string ){
		if( !(this.DOM.messageWrapper instanceof HTMLElement )) return
		const warning = document.createElement('div');
		warning.classList.add('error', 'message1');
		warning.style.margin = "0 auto";
		warning.innerHTML = '<p><strong class="status">Error:</strong> ' + message + '</p>';
		this.DOM.messageWrapper.appendChild(warning);
	}

	async updateItemProperty(key: string, properties: Attributes, quantity?: number){
		try {
			await postCartChange({
				id: key,
				properties: properties,
				quantity: quantity || 1,
			})
		} catch(_err) {
			// fetchJson reports errors to Sentry
		}
	}

	async setLeadTimeProperties(){
		for( const line of window.ffLineItems ){
			const key = line.key
			const lead = line.lead
			if( !lead || !key ) continue;
			const properties: Attributes = {}
			for ( const property of line.properties ){
				properties[property[0]] = property[1]
			}
			const formatter = document.createElement("p") //handle html characters like &nbsp;
			formatter.innerHTML = lead
			const formattedLead = formatter.innerText
			properties.lead = formattedLead
			await this.updateItemProperty( key, properties, line.quantity)

		}
	}
}

window.ffCart = new FFCart()
window.ffCart.init()